
import { TimeEntry } from "./TimeEntryForm";
import { Project } from "@/types/Project";
import { format, startOfMonth, endOfMonth } from "date-fns";

interface InvoiceProps {
  entries: TimeEntry[];
  projects?: Project[];
  month: Date;
}

export function Invoice({ entries, projects = [], month }: InvoiceProps) {
  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);
  
  const monthEntries = entries.filter(entry => {
    const entryDate = new Date(entry.date);
    return entryDate >= monthStart && entryDate <= monthEnd;
  });

  const projectTotals = monthEntries.reduce((acc, entry) => {
    if (entry.projectId) {
      acc[entry.projectId] = (acc[entry.projectId] || 0) + entry.minutes;
    }
    return acc;
  }, {} as Record<string, number>);

  // Calculate total billing using project-specific hourly rates and fixed components
  const projectBillingDetails = Object.entries(projectTotals).map(([projectId, minutes]) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return null;
    
    const hourlyRate = project.hourlyRate;
    const hours = minutes / 60;
    const hourlyBilling = hours * hourlyRate;
    
    // Get fixed price components if any
    const fixedComponents = project.pricingComponents?.filter(comp => comp.pricingType === 'fixed') || [];
    const fixedBilling = fixedComponents.reduce((sum, comp) => sum + comp.amount, 0);
    
    // Get custom hourly components if any
    const hourlyComponents = project.pricingComponents?.filter(comp => comp.pricingType === 'hourly') || [];
    
    return {
      projectId,
      project,
      hours,
      hourlyRate,
      hourlyBilling,
      fixedComponents,
      hourlyComponents,
      fixedBilling,
      totalBilling: hourlyBilling + fixedBilling
    };
  }).filter(Boolean) as Array<{
    projectId: string;
    project: Project;
    hours: number;
    hourlyRate: number;
    hourlyBilling: number;
    fixedComponents: any[];
    hourlyComponents: any[];
    fixedBilling: number;
    totalBilling: number;
  }>;

  const totalBilling = projectBillingDetails.reduce((sum, detail) => sum + detail.totalBilling, 0);
  const totalMinutes = monthEntries.reduce((acc, entry) => acc + entry.minutes, 0);
  const totalHours = totalMinutes / 60;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">
          Invoice for {format(monthStart, "MMMM yyyy")}
        </h3>
      </div>
      
      <div className="space-y-4">
        <div className="grid gap-4">
          {projectBillingDetails.map(detail => {
            const { 
              projectId, 
              project, 
              hours, 
              hourlyRate, 
              hourlyBilling, 
              fixedComponents,
              hourlyComponents,
              totalBilling 
            } = detail;
            
            return (
              <div key={projectId} className="space-y-2">
                <div className="flex justify-between items-center text-sm font-medium">
                  <span>{project.name}</span>
                  <span>${totalBilling.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                
                <div className="ml-4 space-y-1">
                  <div className="flex justify-between items-center text-sm text-muted-foreground">
                    <span>Base hourly rate: ${hourlyRate.toLocaleString('en-US')}/hr × {hours.toFixed(2)} hours</span>
                    <span>${hourlyBilling.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                  
                  {hourlyComponents.length > 0 && (
                    <div className="text-xs text-muted-foreground">
                      <div>Custom hourly rates:</div>
                      {hourlyComponents.map(comp => (
                        <div key={comp.id} className="flex justify-between items-center ml-2">
                          <span>{comp.name || 'Unnamed component'}: ${comp.amount.toLocaleString('en-US')}/hr</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {fixedComponents.map(comp => (
                    <div key={comp.id} className="flex justify-between items-center text-sm text-muted-foreground">
                      <span>{comp.name || 'Fixed Price Component'}</span>
                      <span>${comp.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  ))}
                </div>
                
                {project.invoiceSettings?.clientName && (
                  <div className="mt-2 text-xs text-muted-foreground">
                    Client: {project.invoiceSettings.clientName}
                    {project.invoiceSettings.clientEmail && ` (${project.invoiceSettings.clientEmail})`}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        <div className="border-t pt-4">
          <div className="flex justify-between items-center font-medium">
            <span>Total</span>
            <span>
              {totalHours.toFixed(2)} hours - ${totalBilling.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        </div>

        <div className="mt-8">
          <h4 className="font-medium mb-4">Time Entries Summary</h4>
          <div className="space-y-2">
            {monthEntries.map((entry) => {
              const project = projects.find(p => p.id === entry.projectId);
              return (
                <div
                  key={entry.id}
                  className="flex items-center justify-between py-2 border-b last:border-0"
                >
                  <div>
                    <span className="font-medium">
                      {format(new Date(entry.date), "MMM d")}
                    </span>
                    <span className="text-sm text-muted-foreground ml-2">
                      {project?.name || "No Project"}
                    </span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {Math.floor(entry.minutes / 60)}h {entry.minutes % 60}m
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
